import type { ReactNode } from "react";

import type { createToaster } from "@ark-ui/react/toast";
import type { Options } from "@zag-js/toast";

type CreateToasterReturn = ReturnType<typeof createToaster<ReactNode>>;

export type ToastApi = {
  success<T extends ReactNode>(options: Options<T>): string;
  error<T extends ReactNode>(options: Options<T>): string;
  warning<T extends ReactNode>(options: Options<T>): string;
  info<T extends ReactNode>(options: Options<T>): string;
  create<T extends ReactNode>(
    options: Options<T> & {
      type: "info" | "warning" | "error" | "success";
    },
  ): string;
} & Omit<
  CreateToasterReturn,
  "success" | "error" | "create" | "warning" | "info"
>;

export function createToastApi(toast: CreateToasterReturn): ToastApi {
  return {
    ...toast,
    success(options) {
      return toast.create({
        type: "success",
        ...options,
        meta: {
          palette: "success",
          ...options.meta,
        },
      });
    },
    error(options) {
      return toast.create({
        type: "error",
        ...options,
        meta: {
          palette: "danger",
          ...options.meta,
        },
      });
    },
    warning(options) {
      return toast.create({
        type: "info",
        ...options,
        meta: {
          type: "warning",
          palette: "warning",
          ...options.meta,
        },
      });
    },
    info(options) {
      return toast.create({
        type: "info",
        ...options,
        meta: {
          palette: "info",
          ...options.meta,
        },
      });
    },
    create(options) {
      return toast.create({
        ...options,
        type: options.type === "warning" ? "custom" : options.type,
      });
    },
  };
}
